import {
  formatCount,
  formatLocalDate,
  formatRatePercent,
  formatTimestamp,
  type LocalDate,
} from '@saay/formatting';
import { Money } from '@saay/blocks';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';

import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { CommandDialog } from '../components/command-dialog.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import {
  Button,
  EmptyState,
  NativeSelect,
  PageHeader,
  Section,
  Skeleton,
  TextField,
  useToast,
} from '../kit.js';
import { marketingLabel } from '../marketing-labels.js';
import type {
  CommissionPayoutAttempt,
  CommissionPayoutExecution,
  CommissionPayoutHold,
  CommissionPayoutHoldRelease,
  MarketingCommissionLine,
} from '../marketing-types.js';
import { canMarket } from '../permissions.js';
import {
  listStatus,
  pageRows,
  setupKeys,
  useCommissionPayoutAttempt,
  useCommissionPayoutAttempts,
  useCommissionPayoutHolds,
  useMarketingCommissions,
  useMarketingRecipients,
} from '../queries.js';
import { reasonSchema } from '../validation.js';
import type { MarketingOutletContext } from './marketing.js';
import { CodeText, MarketingStatus, marketingGuidance, phoneSummary } from './marketing-ui.js';

function showDate(value: string): string {
  return formatLocalDate(value as LocalDate);
}

function commissionLineMobile(row: MarketingCommissionLine) {
  return phoneSummary({
    amountIrr: row.payableAmountIrr,
    meta: (
      <>
        {row.merchant.nameFa}
        {` · ${showDate(row.scheduledPayoutDate)}`}
      </>
    ),
    status: row.status,
    title: <CodeText>{row.sale.saleReference}</CodeText>,
  });
}

const lineColumns: readonly DataTableColumn<MarketingCommissionLine>[] = [
  {
    header: 'فروش',
    id: 'sale',
    render: (row) => <CodeText>{row.sale.saleReference}</CodeText>,
  },
  { header: 'فروشگاه', id: 'merchant', render: (row) => row.merchant.nameFa },
  {
    header: 'بازاریاب',
    id: 'recipient',
    render: (row) => row.recipient.displayNameFa ?? row.recipient.recipientCode,
  },
  {
    header: 'پایه',
    id: 'base',
    render: (row) => (
      <>
        {marketingLabel(row.baseType)} <Money rials={row.baseAmountIrr} />
      </>
    ),
  },
  {
    header: 'درصد',
    id: 'rate',
    render: (row) => formatRatePercent(row.percentage, { invalid: 'raw' }),
  },
  {
    header: 'مبلغ قسط',
    id: 'amount',
    numeric: true,
    render: (row) => <Money rials={row.payableAmountIrr} />,
  },
  { header: 'وضعیت', id: 'status', render: (row) => <MarketingStatus value={row.status} /> },
];

export function MarketingCommissionsPage() {
  usePageTitle('کمیسیون‌ها');
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const list = useMarketingCommissions(status === '' ? {} : { status });
  const rows = pageRows(list.data);
  return (
    <>
      <PageHeader
        description="مبلغ هر قسط از سرور می‌آید. خریدار در این فهرست نیست."
        title="کمیسیون‌ها"
      />
      <QueryState
        empty={<EmptyState title="کمیسیونی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری کمیسیون‌ها…" lines={6} />}
        status={listStatus(list)}
      >
        <Section id="marketing-commissions" title="ردیف‌های کمیسیون">
          <FilterPillBar
            activeFilterCount={status === '' ? 0 : 1}
            label="فیلتر کمیسیون‌ها"
            onClearAll={() => setParams({})}
          >
            <FilterPill
              emptyLabel="همه وضعیت‌ها"
              label="وضعیت"
              onChange={(value) => setParams(value === '' ? {} : { status: value })}
              options={(['PENDING', 'HELD', 'LOCKED', 'PAID', 'CANCELLED'] as const).map(
                (value) => ({ label: marketingLabel(value), value }),
              )}
              value={status}
            />
          </FilterPillBar>
          <AdminListTable
            caption="ردیف‌های کمیسیون"
            columns={lineColumns}
            mobileSummary={commissionLineMobile}
            rowKey={(row) => row.id}
            rows={rows}
          />
        </Section>
      </QueryState>
    </>
  );
}

const attemptColumns: readonly DataTableColumn<CommissionPayoutAttempt>[] = [
  {
    header: 'بازاریاب',
    id: 'recipient',
    render: (row) => (
      <Link to={`/marketing/payouts/${row.id}`}>
        {row.recipient.displayNameFa ?? row.recipient.recipientCode}
      </Link>
    ),
  },
  {
    header: 'مبلغ',
    id: 'amount',
    numeric: true,
    render: (row) => <Money rials={row.amountIrr} />,
  },
  { header: 'وضعیت', id: 'status', render: (row) => <MarketingStatus value={row.status} /> },
  { header: 'تاریخ', id: 'date', render: (row) => showDate(row.batch.scheduledPayoutDate) },
];

export function CommissionPayoutsPage() {
  usePageTitle('پرداخت کمیسیون');
  const { self } = useOutletContext<MarketingOutletContext>();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const list = useCommissionPayoutAttempts(status === '' ? {} : { status });
  const rows = pageRows(list.data);
  const [executing, setExecuting] = useState(false);
  const batchId = rows[0]?.batch.id;
  return (
    <>
      <PageHeader
        actions={
          canMarket(self, 'executeCommissionPayouts') && batchId !== undefined ? (
            <Button onClick={() => setExecuting(true)} type="button">
              اجرای پرداخت
            </Button>
          ) : undefined
        }
        description="پرداخت کمیسیون جدا از تسویهٔ فروشگاه است و کارمزد بانک ندارد."
        title="پرداخت کمیسیون"
      />
      <QueryState
        empty={<EmptyState title="پرداختی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری پرداخت‌ها…" lines={6} />}
        status={listStatus(list)}
      >
        <Section id="commission-payouts" title="تلاش‌های پرداخت">
          <FilterPillBar
            activeFilterCount={status === '' ? 0 : 1}
            label="فیلتر پرداخت‌های کمیسیون"
            onClearAll={() => setParams({})}
          >
            <FilterPill
              emptyLabel="همه وضعیت‌ها"
              label="وضعیت"
              onChange={(value) => setParams(value === '' ? {} : { status: value })}
              options={(['PENDING', 'PROCESSING', 'SUCCEEDED', 'FAILED', 'REVERSED'] as const).map(
                (value) => ({ label: marketingLabel(value), value }),
              )}
              value={status}
            />
          </FilterPillBar>
          <AdminListTable
            caption="تلاش‌های پرداخت کمیسیون"
            columns={attemptColumns}
            mobileSummary={(row) =>
              phoneSummary({
                amountIrr: row.amountIrr,
                meta: showDate(row.batch.scheduledPayoutDate),
                status: row.status,
                title: (
                  <Link to={`/marketing/payouts/${row.id}`}>
                    {row.recipient.displayNameFa ?? row.recipient.recipientCode}
                  </Link>
                ),
              })
            }
            rowKey={(row) => row.id}
            rows={rows}
          />
        </Section>
      </QueryState>
      {executing && batchId !== undefined ? (
        <ExecuteDialog batchId={batchId} onOpenChange={setExecuting} />
      ) : null}
    </>
  );
}

function ExecuteDialog({
  batchId,
  onOpenChange,
}: {
  readonly batchId: string;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return (
    <CommandDialog<{ reason: string }, { reason: string }, CommissionPayoutExecution>
      defaultValues={{ reason: '' }}
      fields={['reason']}
      guidance={marketingGuidance}
      name="marketing.payout.execute"
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.commissionPayoutAttempts });
        toast.success({
          confirmedBy: result,
          title: `پرداخت برای ${formatCount(result.data.attemptCount)} نفر شروع شد`,
        });
      }}
      schema={z.object({ reason: reasonSchema() }) as never}
      send={(client, body, intent) =>
        client.post(
          `/admin/settlement-batches/${encodeURIComponent(batchId)}/commission-execution`,
          { body, intent },
        )
      }
      submitLabel="شروع پرداخت"
      title="اجرای پرداخت کمیسیون"
      toBody={(values) => ({ reason: values.reason.trim() })}
    >
      {(form) => (
        <>
          <p>
            این کار پرداخت را می‌سازد و نتیجه را بعداً از بانک می‌گیرد. تأیید مدیر دیگر لازم نیست.
          </p>
          <TextField
            error={form.formState.errors.reason?.message}
            label="دلیل"
            {...form.register('reason')}
          />
        </>
      )}
    </CommandDialog>
  );
}

export function CommissionPayoutPage() {
  const { attemptId = '' } = useParams();
  const { self } = useOutletContext<MarketingOutletContext>();
  const attempt = useCommissionPayoutAttempt(attemptId);
  const lines = useMarketingCommissions({ commissionPayoutAttemptId: attemptId }, attemptId !== '');
  const data = attempt.data;
  usePageTitle(data?.recipient.displayNameFa ?? 'پرداخت');
  const toast = useToast();
  const [retrying, setRetrying] = useState(false);
  const canRetry = canMarket(self, 'retryCommissionPayoutAttempt') && data?.status === 'FAILED';
  return (
    <>
      <PageHeader
        actions={
          canRetry ? (
            <Button onClick={() => setRetrying(true)} type="button">
              پرداخت دوباره
            </Button>
          ) : undefined
        }
        eyebrow={<Link to="/marketing/payouts">پرداخت‌ها</Link>}
        title={data?.recipient.displayNameFa ?? 'پرداخت'}
      />
      <QueryState
        error={attempt.error}
        onRetry={() => void attempt.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری پرداخت…" lines={6} />}
        status={attempt.status}
      >
        {data === undefined ? null : (
          <div className="flex flex-col gap-4">
            <Section id="payout-attempt" title="تلاش پرداخت">
              <p>
                مبلغ <Money rials={data.amountIrr} />
              </p>
              <p>
                وضعیت <MarketingStatus value={data.status} />
              </p>
              <p>
                شبا <CodeText>{data.payoutAccount.shebaMasked}</CodeText>
              </p>
              <p>{`شروع ${formatTimestamp(data.initiatedAt)}`}</p>
              {data.failureCode === null ? null : <p>علت عدم موفقیت ثبت شده است.</p>}
              <p>{`${formatCount(data.lineCount)} ردیف · تلاش ${formatCount(data.attemptNumber)}`}</p>
            </Section>
            <Section id="payout-lines" title="ردیف‌های این پرداخت">
              <QueryState
                empty={<EmptyState title="ردیفی به این پرداخت وصل نیست" />}
                error={lines.error}
                isEmpty={pageRows(lines.data).length === 0}
                onRetry={() => void lines.refetch()}
                skeleton={<Skeleton label="در حال بارگذاری ردیف‌ها…" lines={3} />}
                status={listStatus(lines)}
              >
                <AdminListTable
                  caption="ردیف‌های پرداخت"
                  columns={lineColumns}
                  mobileSummary={commissionLineMobile}
                  rowKey={(row) => row.id}
                  rows={pageRows(lines.data)}
                />
              </QueryState>
            </Section>
          </div>
        )}
      </QueryState>
      {retrying && data !== undefined ? (
        <CommandDialog<
          { reason: string },
          { expectedVersion: number; reason: string },
          CommissionPayoutAttempt
        >
          defaultValues={{ reason: '' }}
          fields={['reason']}
          guidance={marketingGuidance}
          name="marketing.payout.retry"
          onOpenChange={setRetrying}
          onSuccess={async (result) => {
            await attempt.refetch();
            toast.success({ confirmedBy: result, title: 'پرداخت دوباره ثبت شد' });
          }}
          schema={z.object({ reason: reasonSchema() }) as never}
          send={(client, body, intent) =>
            client.post(`/admin/commission-payout-attempts/${encodeURIComponent(data.id)}/retry`, {
              body,
              intent,
            })
          }
          submitLabel="پرداخت دوباره"
          title="پرداخت دوباره"
          toBody={(values) => ({ expectedVersion: data.version, reason: values.reason.trim() })}
        >
          {(form) => (
            <TextField
              error={form.formState.errors.reason?.message}
              label="دلیل"
              {...form.register('reason')}
            />
          )}
        </CommandDialog>
      ) : null}
    </>
  );
}

const holdColumns: readonly DataTableColumn<CommissionPayoutHold>[] = [
  {
    header: 'بازاریاب',
    id: 'recipient',
    render: (row) => row.recipient.displayNameFa ?? row.recipient.recipientCode,
  },
  { header: 'نوع', id: 'type', render: (row) => marketingLabel(row.holdType) },
  { header: 'وضعیت', id: 'status', render: (row) => <MarketingStatus value={row.status} /> },
  { header: 'دلیل', id: 'reason', render: (row) => row.reason ?? '—' },
];

export function CommissionHoldsPage() {
  usePageTitle('توقف پرداخت کمیسیون');
  const { self } = useOutletContext<MarketingOutletContext>();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const holdType = params.get('holdType') ?? '';
  const list = useCommissionPayoutHolds({
    ...(holdType === '' ? {} : { holdType }),
    ...(status === '' ? {} : { status }),
  });
  const rows = pageRows(list.data);
  const [creating, setCreating] = useState(false);
  const [releasing, setReleasing] = useState<CommissionPayoutHold | null>(null);
  const manage = canMarket(self, 'createCommissionPayoutHold');
  return (
    <>
      <PageHeader
        actions={
          manage ? (
            <Button onClick={() => setCreating(true)} type="button">
              توقف پرداخت
            </Button>
          ) : undefined
        }
        description="توقف کمیسیون، تسویهٔ فروشگاه را نگه نمی‌دارد."
        title="توقف پرداخت کمیسیون"
      />
      <QueryState
        empty={<EmptyState title="توقفی ثبت نشده است" />}
        error={list.error}
        isEmpty={rows.length === 0}
        onRetry={() => void list.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری توقف‌ها…" lines={6} />}
        status={listStatus(list)}
      >
        <Section id="commission-holds" title="توقف‌ها">
          <FilterPillBar
            activeFilterCount={[status, holdType].filter(Boolean).length}
            label="فیلتر توقف‌های پرداخت"
            onClearAll={() => setParams({})}
          >
            <FilterPill
              emptyLabel="همه وضعیت‌ها"
              label="وضعیت"
              onChange={(value) =>
                setParams((current) => {
                  const next = new URLSearchParams(current);
                  if (value === '') next.delete('status');
                  else next.set('status', value);
                  return next;
                })
              }
              options={(['ACTIVE', 'RELEASED'] as const).map((value) => ({
                label: marketingLabel(value),
                value,
              }))}
              value={status}
            />
            <FilterPill
              emptyLabel="همه دلیل‌ها"
              label="دلیل توقف"
              onChange={(value) =>
                setParams((current) => {
                  const next = new URLSearchParams(current);
                  if (value === '') next.delete('holdType');
                  else next.set('holdType', value);
                  return next;
                })
              }
              options={(['MANUAL', 'PAYOUT_ACCOUNT_CHANGE'] as const).map((value) => ({
                label: marketingLabel(value),
                value,
              }))}
              value={holdType}
            />
          </FilterPillBar>
          <AdminListTable
            caption="توقف‌های پرداخت کمیسیون"
            columns={holdColumns}
            mobileSummary={(row) =>
              phoneSummary({
                meta: `${marketingLabel(row.holdType)} · ${formatTimestamp(row.placedAt)}`,
                status: row.status,
                title: row.recipient.displayNameFa ?? row.recipient.recipientCode,
              })
            }
            {...(manage
              ? {
                  rowActions: (row: CommissionPayoutHold) =>
                    row.status === 'ACTIVE'
                      ? [{ label: 'آزاد کردن', onSelect: () => setReleasing(row) }]
                      : [],
                }
              : {})}
            rowKey={(row) => row.id}
            rows={rows}
          />
        </Section>
      </QueryState>
      {creating ? <CreateHoldDialog onOpenChange={setCreating} /> : null}
      {releasing === null ? null : (
        <ReleaseHoldDialog hold={releasing} onOpenChange={() => setReleasing(null)} />
      )}
    </>
  );
}

function CreateHoldDialog({ onOpenChange }: { readonly onOpenChange: (open: boolean) => void }) {
  const recipients = useMarketingRecipients({ status: 'ACTIVE' });
  const queryClient = useQueryClient();
  const toast = useToast();
  return (
    <CommandDialog<
      { reason: string; recipientId: string },
      { reason: string; recipientId: string },
      CommissionPayoutHold
    >
      defaultValues={{ reason: '', recipientId: '' }}
      fields={['reason', 'recipientId']}
      guidance={marketingGuidance}
      name="marketing.hold.create"
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.commissionPayoutHolds });
        toast.success({ confirmedBy: result, title: 'پرداخت متوقف شد' });
      }}
      schema={
        z.object({
          reason: reasonSchema(),
          recipientId: z.string().min(1, 'بازاریاب را انتخاب کنید.'),
        }) as never
      }
      send={(client, body, intent) =>
        client.post(
          `/admin/marketing-recipients/${encodeURIComponent(body.recipientId)}/commission-payout-holds`,
          { body: { reason: body.reason }, intent },
        )
      }
      submitLabel="توقف پرداخت"
      title="توقف پرداخت یک بازاریاب"
      toBody={(values) => ({ reason: values.reason.trim(), recipientId: values.recipientId })}
    >
      {(form) => (
        <>
          <NativeSelect
            label="بازاریاب"
            onChange={(event) => form.setValue('recipientId', event.target.value)}
            options={pageRows(recipients.data).map((recipient) => ({
              label: recipient.user.displayNameFa ?? recipient.recipientCode,
              value: recipient.id,
            }))}
            placeholder="انتخاب کنید"
            value={form.watch('recipientId')}
          />
          <TextField
            error={form.formState.errors.reason?.message}
            label="دلیل"
            {...form.register('reason')}
          />
        </>
      )}
    </CommandDialog>
  );
}

function ReleaseHoldDialog({
  hold,
  onOpenChange,
}: {
  readonly hold: CommissionPayoutHold;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return (
    <CommandDialog<{ reason: string }, CommissionPayoutHoldRelease, CommissionPayoutHold>
      defaultValues={{ reason: '' }}
      fields={['reason']}
      guidance={marketingGuidance}
      name="marketing.hold.release"
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.commissionPayoutHolds });
        toast.success({ confirmedBy: result, title: 'توقف برداشته شد' });
      }}
      schema={z.object({ reason: reasonSchema() }) as never}
      send={(client, body, intent) =>
        client.post(`/admin/commission-payout-holds/${encodeURIComponent(hold.id)}/release`, {
          body,
          intent,
        })
      }
      submitLabel="آزاد کردن"
      title="برداشتن توقف"
      toBody={(values) => ({ expectedVersion: hold.version, reason: values.reason.trim() })}
    >
      {(form) => (
        <TextField
          error={form.formState.errors.reason?.message}
          label="دلیل"
          {...form.register('reason')}
        />
      )}
    </CommandDialog>
  );
}
